import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {EnhancerCell} from "../components/EnhancerCell";
import {gsap} from "gsap";
import {GameUtils} from "../utils/GameUtils";
import {TimelineSprite} from "../../core/animation/TimelineSprite";
import {EnhancerEffects} from "../enums/Enum";
import {Sound} from "../sound/Sound";
import {Game} from "../Game";
import {Config} from "../config/Config";

export class EnhancerView extends PIXI.Container implements StageLayer {
    private _circleElectricEffectContainer: PIXI.Container;
    private _enhancerEffectsContainer: PIXI.Container;
    private _enhancers: EnhancerCell[] = [];

    constructor() {
        super();
        this.name = "EnhancerView"
        this.init();
    }

    public show(duration: number = 0.2, alpha: number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            this.alpha = 0;
            this.visible = true;
        });
        tl.to(this, {alpha: alpha, duration: duration});
        return tl;
    }

    public setCount(value: number): void {
        this._enhancers.forEach((enhancer: EnhancerCell) => {
            enhancer.fireFrameCount = value;
        });
    }

    public open(reelId: number): gsap.core.Timeline {
        return this._enhancers[reelId - 1].open();
    }

    public close(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        this._enhancers.forEach((enhancer: EnhancerCell) => {
            enhancer.fireFrameCount = 0;
            if (enhancer.isOpen) {
                tl.add((enhancer as EnhancerCell).close(), 0);
            } else {
                tl.add((enhancer as EnhancerCell).reSetLines(), 0);
            }
        });
        return tl;
    }

    public hide(duration: number = 0.3): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.to(this, {alpha: 0, duration: duration});
        tl.add(() => {
            this.visible = false;
        });
        return tl;
    }

    public setDarkMode(value: boolean): void {
        this._enhancers.forEach((enhancer: EnhancerCell) => {
            enhancer.setDarkMode(value);
        });
    }

    public getPosition(enhancerReelId: number): PIXI.Point {
        const enhancer: EnhancerCell = this._enhancers[enhancerReelId - 1];
        return new PIXI.Point(this.position.x + enhancer.position.x, this.position.y + enhancer.position.y);
    }

    public playFireBallToEnhancer(reelId: number, rowId: number, enhancerReelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const enhancer: EnhancerCell = this._enhancers[enhancerReelId];
        const pos: PIXI.Point = GameUtils.getSymbolPos(reelId, rowId);
        pos.y = pos.y - this.position.y;
        const destPos: PIXI.Point = new PIXI.Point(enhancer.x, enhancer.y);
        const fireBallTLs: TimelineSprite = GameUtils.createTimelineSprite("fire_ball/fire_ball_render_000", 10, pos, true);
        this.addChild(fireBallTLs);
        fireBallTLs.scale.set(2);

        fireBallTLs.blendMode = PIXI.BLEND_MODES.ADD;
        const angle: number = GameUtils.getAngleInDegree([pos.x, pos.y], [destPos.x, destPos.y - 45]);
        fireBallTLs.angle = angle - 90;
        tl.add([
            () => {
                fireBallTLs.getAnimationAutoShowHide(true, false)
            },
            gsap.to(fireBallTLs, {duration: 0.35, x: destPos.x, y: destPos.y})
        ]);
        tl.add(() => {
            Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.ENHANCER_ACTIVATION_FIRE, Sound.ENHANCER_ACTIVATION_FIRE);
            enhancer.play(EnhancerEffects.FIRE_START, false);
        }, 0.15);
        tl.add(() => {
            gsap.to(fireBallTLs.scale, {duration: 0.1, x: 0.4, y: 0.4})
        }, 0.25);
        tl.add(() => {

            enhancer.play(EnhancerEffects.FIRE_LOOP, true);
            gsap.to(fireBallTLs, {
                duration: 0.1, alpha: 0, onComplete: () => {
                    fireBallTLs.parent.removeChild(fireBallTLs.parent.removeChild(fireBallTLs));
                }
            });
        }, 0.65);
        return tl;
    }

    public playFireOnEnhancer(enhancerReelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const enhancer: EnhancerCell = this._enhancers[enhancerReelId];
        tl.add(() => {
            Game.soundPlayer.avoidFrequentPlaySoundAndPlayLastQueued(Sound.ENHANCER_ACTIVATION_FIRE, Sound.ENHANCER_ACTIVATION_FIRE);
            enhancer.setActive();
            enhancer.play(EnhancerEffects.FIRE_START, false);
        }, 0);
        tl.add(() => {
            enhancer.play(EnhancerEffects.FIRE_LOOP, false);
        }, 0.5);
        tl.add(() => {
            enhancer.play(EnhancerEffects.FIRE_LOOP, true);
        });
        return tl;
    }

    public playLightToEnhancer(reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const enhancer: EnhancerCell = this._enhancers[reelId - 1];
        const circleElectricEffect: TimelineSprite = <TimelineSprite>this._circleElectricEffectContainer.children[this.getEnhancerIdByReelId(reelId)];
        const pos: PIXI.Point = circleElectricEffect.position;
        const destPos: PIXI.Point = new PIXI.Point(enhancer.x, enhancer.y);
        const lightingEffect: TimelineSprite = GameUtils.createTimelineSprite("enhancer_cell_lightning_effect/enhancer_cell_lightning_effect_000", 30, pos);
        this.addChild(lightingEffect);
        lightingEffect.anchor.set(0.5, 0);
        lightingEffect.blendMode = PIXI.BLEND_MODES.ADD;
        const angle: number = GameUtils.getAngleInDegree([pos.x, pos.y], [destPos.x, destPos.y - 45]);
        const distance: number = GameUtils.getDistance(pos, destPos);
        lightingEffect.angle = angle - 90;
        lightingEffect.scale.y = (distance + 20) / lightingEffect.height;
        tl.add([
            () => circleElectricEffect.hide(),
            lightingEffect.getAnimationAutoShowHide(true, false)
        ]);
        tl.add(() => {
            lightingEffect.parent.removeChild(lightingEffect);
            circleElectricEffect.parent.removeChild(circleElectricEffect);
        });
        return tl;
    }

    private getEnhancerIdByReelId(reelId: number): number {
        if (reelId === 3) {
            return 1;
        } else if (reelId === 2) {
            return 2
        } else {
            return reelId - 1;
        }
    }

    private init(): void {
        this.position.y = 167 + Config.MARGIN_TOP;
        this.createBottomEnhancer();
        this.visible = false;
    }


    private createBottomEnhancer(): void {
        const enhancerContainer: PIXI.Container = new PIXI.Container();
        enhancerContainer.name = "EnhancerContainer";
        this._enhancerEffectsContainer = new PIXI.Container();
        this._enhancerEffectsContainer.name = "EnhancerEffectsContainer";

        const bottomYPos: number = 495; //99 + (198*2)
        let bottomXPos: number = 72;
        for (let reelId: number = 1; reelId <= 3; reelId++) {
            const effects: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("enhancerEffects"));
            effects.visible = false;
            effects.position.set(bottomXPos + (reelId * 144), bottomYPos);
            effects.scale.set(0.9);
            this._enhancerEffectsContainer.addChild(effects);

            const enhancer: EnhancerCell = new EnhancerCell(reelId, effects);
            enhancerContainer.addChild(enhancer);
            enhancer.position.set(bottomXPos + (reelId * 144), bottomYPos);
            this._enhancers.push(enhancer);
        }

        this._circleElectricEffectContainer = new PIXI.Container();
        this._circleElectricEffectContainer.name = "CIRCLE_EFFECT_CONTAINER";
        this.addChild(enhancerContainer, this._circleElectricEffectContainer, this._enhancerEffectsContainer);
    }

}